/**
 * The conversation in the help panel (D10-UI-002/005, D10-SEC-005).
 *
 * A `role="log"` live region marked busy while a reply streams, so a screen
 * reader announces each finished reply once rather than every token. A reply
 * goes through the one safe renderer for its whole life (#3675): while it
 * streams, the renderer holds back or provisionally closes a half-typed marker
 * on the last line, so the text is formatted from the first token and never
 * changes style when the reply completes. Until the first token arrives a
 * three-dot "thinking" indicator stands in. A reply that carried an action
 * (D10-ACT, #3605) shows a chip to run it again, or the note saying why it
 * could not run here. An "Open the page" chip is left out while the user is
 * already on that page (#3687): the navigate ran, so a button to do it again
 * beside "Opening <page> for you now." would only read as if it had not.
 */
import { useEffect, useRef } from "react";
import { AlertCircle, Bug, GraduationCap, MousePointerClick, SquareArrowOutUpRight } from "lucide-react";
import type { ChatMessage } from "../../contexts/supportChat";
import { renderReply } from "./renderReply";

interface Props {
  messages: ChatMessage[];
  streaming: boolean;
  awaitingFirstToken: boolean;
  allowedRoutes: readonly string[];
  onLinkClick: () => void;
  /** Run a completed reply's action again. */
  onRunAction?: (messageId: string) => void;
  /** The assistant's configured name, used in the "thinking" announcement. */
  assistantName?: string;
  /** The page the user is on (hides a navigate chip for this very page, #3687). */
  currentRoute?: string;
}

const ACTION_LABELS = {
  start_tour: { label: "Start the tour", Icon: GraduationCap },
  open_feedback: { label: "Open the report form", Icon: Bug },
  navigate: { label: "Open the page", Icon: SquareArrowOutUpRight },
  highlight: { label: "Show me again", Icon: MousePointerClick },
} as const;

export function SupportMessageList({
  messages,
  streaming,
  awaitingFirstToken,
  allowedRoutes,
  onLinkClick,
  onRunAction,
  assistantName = "The assistant",
  currentRoute,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  const lastText = messages[messages.length - 1]?.text;

  // Keep the newest text in view as it streams in. An empty conversation is
  // left alone: the panel keeps its greeting in view at the top (#3684).
  useEffect(() => {
    if (messages.length === 0) return;
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length, lastText]);

  return (
    <div
      role="log"
      aria-live="polite"
      aria-busy={streaming}
      aria-label="Conversation"
      className="flex flex-col gap-3"
    >
      {messages.map((m) => {
        if (m.role === "user") {
          return (
            <div
              key={m.id}
              data-support-role="user"
              className="ml-8 self-end rounded-lg rounded-br-sm bg-[var(--color-accent)] px-3 py-2 text-sm text-[#ffffff] whitespace-pre-wrap break-words"
            >
              <span className="sr-only">You said: </span>
              {m.text}
            </div>
          );
        }
        const waiting = m.status === "streaming" && !m.text;
        return (
          <div
            key={m.id}
            data-support-role="assistant"
            data-status={m.status}
            className={
              "mr-8 self-start rounded-lg rounded-bl-sm border px-3 py-2 text-sm break-words " +
              (m.status === "error"
                ? "border-[var(--color-danger)]/40 bg-[var(--color-bg-page)] text-[var(--color-text-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-bg-page)] text-[var(--color-text-primary)]")
            }
          >
            {waiting && awaitingFirstToken ? (
              <ThinkingIndicator assistantName={assistantName} />
            ) : m.status === "streaming" ? (
              // #3675: the same renderer as the finished reply, told the text
              // is still arriving, so nothing changes style when it completes.
              <div>{renderReply(m.text, { allowedRoutes, onLinkClick, partial: true })}</div>
            ) : m.status === "error" ? (
              <div className="flex items-start gap-2">
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-danger)]"
                  aria-hidden="true"
                />
                <div className="min-w-0">{renderReply(m.text, { allowedRoutes, onLinkClick })}</div>
              </div>
            ) : (
              <div>
                {renderReply(m.text, { allowedRoutes, onLinkClick })}
                {m.truncated && (
                  <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                    That answer was cut short. Ask me to continue if you need more.
                  </p>
                )}
                {m.action &&
                  (m.actionNote ? (
                    <p className="mt-2 text-xs text-[var(--color-text-muted)]">{m.actionNote}</p>
                  ) : (
                    onRunAction &&
                    !alreadyThere(m, currentRoute) && (
                      <ActionChip message={m} onRun={onRunAction} />
                    )
                  ))}
              </div>
            )}
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

/**
 * Three dots that fade in sequence while the first words of a reply are on
 * their way. Announced once as "<assistant> is thinking" (the dots themselves
 * are decorative); reduced-motion users get static dots via the CSS. The label
 * comes after the dots so the CSS `:nth-child` stagger lands on dots 1-3.
 */
function ThinkingIndicator({ assistantName }: { assistantName: string }) {
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={`${assistantName} is thinking`}
      data-testid="support-thinking"
      className="flex h-5 items-center gap-1"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          aria-hidden="true"
          className="crm-thinking-dot block h-1.5 w-1.5 rounded-full bg-[var(--color-text-muted)]"
        />
      ))}
      <span className="sr-only">{assistantName} is thinking</span>
    </span>
  );
}

/** The reply's navigate already landed the user on its page (#3687). */
function alreadyThere(message: ChatMessage, currentRoute?: string): boolean {
  const action = message.action;
  return (
    !!currentRoute && action?.name === "navigate" && action.args.route === currentRoute
  );
}

function ActionChip({
  message,
  onRun,
}: {
  message: ChatMessage;
  onRun: (messageId: string) => void;
}) {
  if (!message.action) return null;
  const { label, Icon } = ACTION_LABELS[message.action.name];
  return (
    <button
      type="button"
      onClick={() => onRun(message.id)}
      data-testid="support-action-chip"
      className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
    >
      <Icon className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
      {label}
    </button>
  );
}
